/**
 * fileIO.js — File I/O data layer for the .delb format.
 *
 * Exports:
 *   serializeDelb    — pure function, produces .delb JSON-ready object
 *   saveAs           — triggers browser download of diagram.delb
 *   openFile         — opens the hidden file input, reads and parses result
 *   openUnderlayFile — opens the hidden PNG input, loads image and calls onLoaded(img, filename)
 *   validateDelb     — returns array of error strings for a parsed .delb object
 *   confirmUnsavedChanges — Promise-based dialog: "save" | "discard" | "cancel"
 *   promptUnderlayLocate — Promise-based dialog: lets user locate or skip underlay PNG
 *   markDirty        — sets dirty flag
 *   clearDirty       — resets dirty flag
 *   isDirty          — returns current dirty flag value
 */

// ---------------------------------------------------------------------------
// Dirty flag state
// ---------------------------------------------------------------------------

let _dirty = false;

/**
 * Mark the document as having unsaved changes.
 */
export function markDirty() {
  _dirty = true;
}

/**
 * Clear the unsaved-changes flag (call after successful save or discard).
 */
export function clearDirty() {
  _dirty = false;
}

/**
 * Return whether there are unsaved changes.
 *
 * @returns {boolean}
 */
export function isDirty() {
  return _dirty;
}

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

/**
 * Produce the .delb JSON-ready object. Does NOT stringify.
 *
 * The optional underlay parameters allow the caller to persist the underlay
 * image. When underlayDataUrl is falsy the header.underlay field is omitted
 * entirely (backward-compatible).
 *
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @param {Array<{id: string, vertices: Array<{lat: number, lng: number}>, fillColor: string, fillColorHex: string, zIndex?: number}>} polygons
 * @param {{ scale: number, offsetX: number, offsetY: number }} viewport
 * @param {string|null|undefined} [underlayDataUrl] - base64 data URL of the underlay PNG, or falsy to omit
 * @param {number} [underlayScale] - underlay scale factor (used only when underlayDataUrl is truthy)
 * @param {string|null|undefined} [underlayFilename] - original filename (metadata only)
 * @returns {{ header: object, polygons: Array<object> }}
 */
export function serializeDelb(bbox, polygons, viewport, underlayDataUrl, underlayScale, underlayFilename) {
  return {
    header: {
      version: 1,
      boundingBox: bbox,
      zoom: viewport.scale,
      lastUpdated: new Date().toISOString(),
    },
    polygons: polygons.map((p, i) => ({
      id: p.id,
      vertices: p.vertices,
      zIndex: p.zIndex ?? i,
      fillColor: p.fillColor,
      fillColorHex: p.fillColorHex,
    })),
    ...(underlayDataUrl ? { underlay: { dataUrl: underlayDataUrl, scale: underlayScale, filename: underlayFilename || null } } : {}),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate a parsed .delb object. Returns array of error strings.
 * An empty array means the data is valid.
 *
 * Does NOT error on version > 1 — callers are responsible for showing
 * a version warning when needed.
 *
 * @param {*} data
 * @returns {string[]}
 */
export function validateDelb(data) {
  const errors = [];

  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    errors.push("Data must be a non-null object");
    return errors;
  }

  if (!data.header || typeof data.header !== "object") {
    errors.push("Missing or invalid header");
  } else if (typeof data.header.version !== "number") {
    errors.push("header.version must be a number");
  }

  if (!Array.isArray(data.polygons)) {
    errors.push("polygons must be an array");
  }

  // Validate optional underlay field (if present)
  if (data.underlay !== undefined) {
    const u = data.underlay;
    if (u === null || typeof u !== "object" || Array.isArray(u)) {
      errors.push("header.underlay must be a non-null object");
    } else {
      if (typeof u.dataUrl !== "string") {
        errors.push("header.underlay.dataUrl must be a string");
      }
      if (typeof u.scale !== "number") {
        errors.push("header.underlay.scale must be a number");
      }
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Download (saveAs)
// ---------------------------------------------------------------------------

/**
 * Serialize the current diagram and trigger a browser download as "diagram.delb".
 *
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @param {Array<object>} polygons
 * @param {{ scale: number }} viewport
 * @param {string|null|undefined} [underlayDataUrl] - base64 data URL of the underlay PNG, or falsy to omit
 * @param {number} [underlayScale] - underlay scale factor
 * @param {string|null|undefined} [underlayFilename] - original filename (metadata only)
 */
export function saveAs(bbox, polygons, viewport, underlayDataUrl, underlayScale, underlayFilename) {
  const data = serializeDelb(bbox, polygons, viewport, underlayDataUrl, underlayScale, underlayFilename);
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "diagram.delb";
  anchor.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Open file
// ---------------------------------------------------------------------------

/**
 * Display a local alert with an error message (v1 — simple fallback).
 *
 * @param {string} message
 */
function showLoadError(message) {
  window.alert(message);
}

/**
 * Reset and activate the hidden file input, then parse the chosen file and
 * call onLoaded(data) with the parsed JSON.
 *
 * @param {function(object): void} onLoaded - Called with parsed JSON data
 */
export function openFile(onLoaded) {
  const input = document.getElementById("file-open-input");
  if (!input) return;
  input.value = "";
  input.onchange = () => {
    const file = input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        onLoaded(data);
      } catch {
        showLoadError("File is not valid JSON");
      }
    };
    reader.readAsText(file);
  };
  input.click();
}

// ---------------------------------------------------------------------------
// Unsaved-changes dialog
// ---------------------------------------------------------------------------

/**
 * Show the unsaved-changes dialog and return a Promise that resolves to
 * "save", "discard", or "cancel".
 *
 * Follows the same cleanup pattern as confirmDelete() in application.js:
 * a single cleanup function removes all listeners, closes the dialog, and
 * resolves the promise.
 *
 * @returns {Promise<"save"|"discard"|"cancel">}
 */
export function confirmUnsavedChanges() {
  return new Promise((resolve) => {
    const dialog = document.getElementById("unsaved-changes-dialog");
    const saveBtn = document.getElementById("unsaved-save");
    const discardBtn = document.getElementById("unsaved-discard");
    const cancelBtn = document.getElementById("unsaved-cancel");

    function cleanup(result) {
      saveBtn.removeEventListener("click", onSave);
      discardBtn.removeEventListener("click", onDiscard);
      cancelBtn.removeEventListener("click", onCancel);
      dialog.removeEventListener("cancel", onDialogCancel);
      dialog.close();
      resolve(result);
    }

    function onSave() { cleanup("save"); }
    function onDiscard() { cleanup("discard"); }
    function onCancel() { cleanup("cancel"); }
    function onDialogCancel(e) { e.preventDefault(); cleanup("cancel"); }

    saveBtn.addEventListener("click", onSave);
    discardBtn.addEventListener("click", onDiscard);
    cancelBtn.addEventListener("click", onCancel);
    dialog.addEventListener("cancel", onDialogCancel);

    dialog.showModal();
  });
}

// ---------------------------------------------------------------------------
// Underlay file open
// ---------------------------------------------------------------------------

/**
 * Open the hidden PNG file input, load the selected image, and call onLoaded.
 *
 * @param {function(HTMLImageElement, string): void} onLoaded - Called with loaded image and filename
 */
export function openUnderlayFile(onLoaded) {
  const input = document.getElementById("underlay-open-input");
  if (!input) return;
  input.value = "";
  input.onchange = () => {
    const file = input.files[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      onLoaded(img, file.name);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  };
  input.click();
}

// ---------------------------------------------------------------------------
// Underlay missing-file dialog
// ---------------------------------------------------------------------------

/**
 * Show the underlay-locate dialog and return a Promise that resolves with
 * {img, filename} if the user selects a file, or null if they skip.
 *
 * @param {string} [filename] - Original filename to show in the message
 * @returns {Promise<{img: HTMLImageElement, filename: string}|null>}
 */
export function promptUnderlayLocate(filename) {
  return new Promise((resolve) => {
    const dialog    = document.getElementById("underlay-missing-dialog");
    const skipBtn   = document.getElementById("underlay-missing-ok");
    const browseBtn = document.getElementById("underlay-missing-browse");
    const msgEl     = document.getElementById("underlay-missing-msg");
    const fileInput = document.getElementById("underlay-open-input");

    if (msgEl && filename) {
      msgEl.textContent = `This file references an underlay image "${filename}". Please locate it to restore the underlay.`;
    }

    function cleanup() {
      skipBtn.removeEventListener("click", onSkip);
      browseBtn.removeEventListener("click", onBrowse);
      dialog.removeEventListener("cancel", onDialogCancel);
      fileInput.onchange = null;
      dialog.close();
    }

    function onSkip() { cleanup(); resolve(null); }
    function onDialogCancel(e) { e.preventDefault(); cleanup(); resolve(null); }

    function onBrowse() {
      fileInput.value = "";
      fileInput.onchange = () => {
        const file = fileInput.files[0];
        if (!file) return;
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          cleanup();
          resolve({ img, filename: file.name });
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
      };
      fileInput.click();
    }

    skipBtn.addEventListener("click", onSkip);
    browseBtn.addEventListener("click", onBrowse);
    dialog.addEventListener("cancel", onDialogCancel);

    dialog.showModal();
  });
}
